// Lightweight line-level diff for the HistoryPanel side-by-side.
// Walter 2026-05-17: color-coded diff between "current" and "selected
// previous version" so it's visually obvious which lines were added
// or removed.
//
// We deliberately use multiset comparison rather than a true LCS /
// Myers diff:
//   - Lines in `a` not present in `b` → tagged 'removed' (only-in-a).
//   - Lines in `b` not present in `a` → tagged 'added' (only-in-b).
//   - Lines present in both          → tagged 'common'.
//
// Trade-off: doesn't recognise moved blocks (a line that's been
// reordered will show up as both removed and added). For the conflict-
// merge case lists are top-down todo items rather than prose; line
// identity is high and reordering is rare, so multiset is plenty.
// If/when we want true diff (Myers / patience), drop in jsdiff.
//
// Each side gets its OWN tagged list so the panels can render
// independently — the left panel uses `aTagged`, the right uses
// `bTagged`. Common lines on both sides are tagged 'common' with
// matching counts (we consume from the shared pool so duplicate
// lines align rather than mass-tagging duplicates as common).

export function diffLines(aText, bText) {
  const a = (aText || '').split('\n');
  const b = (bText || '').split('\n');

  // Count occurrences on each side so we can consume matches.
  const bCounts = new Map();
  for (const line of b) {
    bCounts.set(line, (bCounts.get(line) || 0) + 1);
  }

  const aTagged = a.map(line => {
    const c = bCounts.get(line) || 0;
    if (c > 0) {
      bCounts.set(line, c - 1);
      return { text: line, tag: 'common' };
    }
    return { text: line, tag: 'removed' };
  });

  // Rebuild bCounts for the b pass — we consumed it above. This time,
  // an `a`-line consumes one occurrence on the `a` side per match;
  // any leftover `b` lines after that are tagged 'added'.
  const aCounts = new Map();
  for (const line of a) {
    aCounts.set(line, (aCounts.get(line) || 0) + 1);
  }

  const bTagged = b.map(line => {
    const c = aCounts.get(line) || 0;
    if (c > 0) {
      aCounts.set(line, c - 1);
      return { text: line, tag: 'common' };
    }
    return { text: line, tag: 'added' };
  });

  return { aTagged, bTagged };
}
